import { useEffect, useRef, useState } from 'react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Modal, Typography } from '@mui/material';
import { useAccessibility } from '../../../../../Store/contexts/AccessibilityContext';
import DefaultButton from '../../../../../components/Accessibility/DefaultButton';
import {
  TERMO_TRANSCRICAO_PARAGRAFOS,
  TERMO_TRANSCRICAO_TITULO,
} from './termosTranscricaoContent';
import { sxTermosUsoModal } from './termosUsoModalSx';
import './styles.css';

const SCROLL_TOLERANCE_PX = 200;

type TermosUsoModalBaseProps = {
  open: boolean;
  onClose: () => void;
};

type TermosUsoModalAcceptProps = TermosUsoModalBaseProps & {
  variant?: 'accept';
  onConfirm: () => void;
  isSubmitting?: boolean;
};

type TermosUsoModalViewProps = TermosUsoModalBaseProps & {
  variant: 'view';
};

export type TermosUsoModalProps = TermosUsoModalAcceptProps | TermosUsoModalViewProps;

const TermosUsoModal: React.FC<TermosUsoModalProps> = (props) => {
  const { open, onClose } = props;
  const isViewMode = props.variant === 'view';
  const onConfirm = !isViewMode ? props.onConfirm : undefined;
  const isSubmitting = !isViewMode ? (props.isSubmitting ?? false) : false;

  const accessibility = useAccessibility();
  const termosRef = useRef<HTMLDivElement>(null);
  const [leuTermos, setLeuTermos] = useState(false);

  useEffect(() => {
    if (!open || isViewMode) {
      setLeuTermos(false);
      return;
    }

    const timer = setTimeout(() => {
      const elemento = termosRef.current;
      elemento?.focus();

      if (
        elemento &&
        elemento.scrollHeight <= elemento.clientHeight + SCROLL_TOLERANCE_PX
      ) {
        setLeuTermos(true);
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [open, isViewMode]);

  useEffect(() => {
    if (!open || !isViewMode) return;

    const timer = setTimeout(() => {
      termosRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [open, isViewMode]);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    if (isViewMode || leuTermos) return;

    const target = event.currentTarget;
    const chegouAoFim =
      target.scrollHeight - target.scrollTop <= target.clientHeight + SCROLL_TOLERANCE_PX;

    if (chegouAoFim) {
      setLeuTermos(true);
    }
  };

  const handleConfirm = () => {
    if (isViewMode || !leuTermos || isSubmitting || !onConfirm) return;
    onConfirm();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="termos-transcricao-titulo"
      aria-describedby="termos-transcricao-conteudo"
    >
      <Box
        sx={sxTermosUsoModal}
        style={{ ...accessibility.contrastStyle, ...accessibility.contrastBorderAccessibility }}
      >
        <div className="transcricao-termos-modal">
          <div className="transcricao-termos-modal-header">
            <Typography
              id="termos-transcricao-titulo"
              component="h2"
              className="transcricao-termos-modal-titulo"
              style={accessibility.textStyleAccessibility}
            >
              Termos de uso
            </Typography>
            <button
              type="button"
              className="transcricao-termos-modal-fechar"
              onClick={onClose}
              aria-label="Fechar termos de uso"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <div className="transcricao-termos-conteudo-holder">
            <div
              id="termos-transcricao-conteudo"
              ref={termosRef}
              className="transcricao-termos-conteudo"
              onScroll={isViewMode ? undefined : handleScroll}
              tabIndex={0}
              style={accessibility.textStyleAccessibility}
            >
              <Typography component="h3" className="transcricao-termos-subtitulo">
                {TERMO_TRANSCRICAO_TITULO}
              </Typography>
              {TERMO_TRANSCRICAO_PARAGRAFOS.map((paragrafo, index) => (
                <Typography key={index} component="p" className="transcricao-termos-paragrafo">
                  {paragrafo}
                </Typography>
              ))}
            </div>
          </div>

          {!isViewMode && (
            <DefaultButton
              text={isSubmitting ? 'SALVANDO...' : 'ESTOU CIENTE E DE ACORDO'}
              onClick={handleConfirm}
              disabled={!leuTermos || isSubmitting}
              fullWidth
              className="transcricao-termos-botao-confirmar"
            />
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default TermosUsoModal;
